import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import LoadingAnimation from './LoadingAnimation';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cores das ilhas conforme briefing seção 9.2
// Ordem segue island.order (1..5). Cor das folhas é usada nas barras/radar.
const ISLAND_LEAF_COLORS = ['#C4B5FD', '#FCA5A5', '#93C5FD', '#FDE68A', '#86EFAC'];
const ISLAND_POT_COLORS  = ['#7C3AED', '#DC2626', '#2563EB', '#D97706', '#16A34A'];

// ── Radar de competências (5 ilhas, 0-100%) ───────────────────────────────
const CompetencyRadar = ({ islandsWithProgress }) => {
  const labels = islandsWithProgress.map(isl => {
    // Nome curto pra caber no radar — primeira palavra, ou nome inteiro se curto
    const name = isl.name || `Ilha ${isl.order}`;
    if (name.length <= 14) return name;
    return name.split(' ')[0];
  });

  const data = {
    labels,
    datasets: [{
      label: 'Progresso (%)',
      data: islandsWithProgress.map(isl => isl.progress || 0),
      backgroundColor: 'rgba(83, 74, 183, 0.18)',
      borderColor: '#534AB7',
      borderWidth: 2,
      pointBackgroundColor: '#534AB7',
      pointRadius: 4,
    }],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      r: {
        min: 0, max: 100,
        ticks: { stepSize: 25, font: { size: 9 }, color: '#9CA3AF', backdropColor: 'transparent' },
        grid: { color: '#E5E7EB' },
        pointLabels: { font: { size: 11 }, color: '#374151' },
      },
    },
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => `${ctx.raw}% concluído` } } },
  };
  return <Radar data={data} options={options} />;
};

// ──────────────────────────────────────────────────────────────────────────
// ProgressDashboard — Painel "Meu Progresso" (rota /progress)
// Layout conforme briefing seção 8.1 — Painel do Colaborador, aba "Início":
//   • Cards de métricas (acácias florescidas, ecos da semana, recursos)
//   • Mapa de Competências (radar 5 ilhas)
//   • Progresso na Trilha (barras horizontais por ilha)
// Todos os dados vêm do backend — nada mockado.
// ──────────────────────────────────────────────────────────────────────────
const ProgressDashboard = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [islands, setIslands] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [pots, setPots] = useState([]);
  const [resources, setResources] = useState(null);
  const [ecosCount, setEcosCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [islandsRes, lessonsRes, potsRes, resRes, ecosRes] = await Promise.all([
          axios.get(`${API}/islands`, { withCredentials: true }),
          axios.get(`${API}/lessons`, { withCredentials: true }),
          axios.get(`${API}/gamification/my-pots`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/gamification/my-resources`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/eco/my-ecos?days=7`, { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
        ]);
        if (cancelled) return;
        setIslands(islandsRes.data || []);
        setLessons(lessonsRes.data || []);
        setPots(potsRes.data || []);
        setResources(resRes.data || null);
        setEcosCount(ecosRes.data?.count ?? ecosRes.data?.length ?? 0);
      } catch (err) {
        console.error('ProgressDashboard fetch error:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, []);

  const isLessonCompleted = useCallback((lessonId) =>
    authUser?.completedLessons?.includes(lessonId) ?? false,
  [authUser]);

  const getLessonsForIsland = useCallback((islandId) =>
    lessons
      .filter(l => l.island_id === islandId)
      .sort((a, b) => (a.order || 0) - (b.order || 0)),
  [lessons]);

  const getIslandProgress = useCallback((island) => {
    const islandLessons = getLessonsForIsland(island.island_id);
    if (!islandLessons.length) return 0;
    const done = islandLessons.filter(l => isLessonCompleted(l.lesson_id)).length;
    return Math.round((done / islandLessons.length) * 100);
  }, [getLessonsForIsland, isLessonCompleted]);

  const islandsWithProgress = useMemo(() =>
    [...islands]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((island, index) => ({
        ...island,
        order: island.order ?? index + 1,
        progress: getIslandProgress(island),
        leafColor: ISLAND_LEAF_COLORS[index % ISLAND_LEAF_COLORS.length],
        potColor:  ISLAND_POT_COLORS[index % ISLAND_POT_COLORS.length],
      })),
  [islands, getIslandProgress]);

  // Posição atual na trilha — primeira lição não-concluída na primeira ilha não-concluída
  const currentPosition = useMemo(() => {
    for (const island of islandsWithProgress) {
      const islandLessons = getLessonsForIsland(island.island_id);
      const nextIdx = islandLessons.findIndex(l => !isLessonCompleted(l.lesson_id));
      if (nextIdx !== -1) {
        return { islandName: island.name, lessonNumber: nextIdx + 1, lessonId: islandLessons[nextIdx].lesson_id };
      }
    }
    return null;
  }, [islandsWithProgress, getLessonsForIsland, isLessonCompleted]);

  // Acácias florescidas = vasos no estágio 4
  const acaciasFlorescidas = pots.filter(p => p.current_stage === 4).length;

  if (isLoading || !authUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-white">
        <LoadingAnimation size="md" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* ── Header: saudação + posição atual na trilha ─────────────────── */}
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Painel do Colaborador
          </p>
          <Card className="p-6">
            <div className="mb-5">
              <h1 className="text-2xl font-bold text-gray-900">
                Olá, {authUser?.name?.split(' ')[0] || 'Estudante'}
              </h1>
              {currentPosition ? (
                <p className="text-gray-600 text-sm mt-1">
                  {currentPosition.islandName} · Lição {currentPosition.lessonNumber}
                </p>
              ) : islandsWithProgress.length > 0 ? (
                <p className="text-gray-600 text-sm mt-1">
                  Você concluiu todas as lições disponíveis 🎉
                </p>
              ) : (
                <p className="text-gray-600 text-sm mt-1">
                  Sua jornada relacional começa aqui
                </p>
              )}
            </div>

            {/* ── Cards de métricas ──────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Acácias florescidas */}
              <div className="bg-purple-50 border border-purple-100 rounded-2xl p-4 flex flex-col items-center text-center">
                <div className="text-3xl mb-1">🌸</div>
                <div className="text-3xl font-bold text-gray-900">{acaciasFlorescidas}</div>
                <div className="text-xs text-gray-600 mt-1">acácias florescidas</div>
              </div>

              {/* Ecos esta semana */}
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex flex-col items-center text-center">
                <div className="text-3xl mb-1">💧</div>
                <div className="text-3xl font-bold text-gray-900">{ecosCount}</div>
                <div className="text-xs text-gray-600 mt-1">ecos esta semana</div>
              </div>

              {/* Recursos */}
              <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex flex-col items-center text-center">
                <div className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-1">
                  <span>🪣 {resources?.watering_cans ?? 0}</span>
                  <span className="text-gray-300">·</span>
                  <span>💧 {resources?.waterings ?? 0}</span>
                  <span className="text-gray-300">·</span>
                  <span>🌿 {resources?.active_fertilizers ?? 0}</span>
                </div>
                <div className="text-xs text-gray-600 mt-1">seus recursos</div>
              </div>
            </div>

            {/* ── Mapa de competências + Progresso na trilha ─────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
              {/* Mapa de competências (radar) */}
              <div className="bg-white border border-gray-200 rounded-2xl p-5">
                <h2 className="text-sm font-semibold text-gray-800 mb-3">Mapa de competências</h2>
                {islandsWithProgress.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">
                    Nenhuma ilha disponível ainda.
                  </p>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="flex-1 max-w-[260px]">
                      <CompetencyRadar islandsWithProgress={islandsWithProgress} />
                    </div>
                    <ul className="text-xs space-y-1.5 text-gray-700">
                      {islandsWithProgress.map((isl, idx) => (
                        <li key={isl.island_id} className="flex items-center gap-2">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ background: isl.potColor }}
                          />
                          <span className="truncate">{isl.name || `Ilha ${idx + 1}`}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Progresso na trilha (barras horizontais por ilha) */}
              <div className="bg-white border border-gray-200 rounded-2xl p-5">
                <h2 className="text-sm font-semibold text-gray-800 mb-3">Progresso na trilha</h2>
                {islandsWithProgress.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">
                    Nenhuma ilha disponível ainda.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {islandsWithProgress.map((island, idx) => {
                      const targetLessonId = (() => {
                        const islandLessons = getLessonsForIsland(island.island_id);
                        const next = islandLessons.find(l => !isLessonCompleted(l.lesson_id));
                        return next?.lesson_id || islandLessons[0]?.lesson_id;
                      })();
                      return (
                        <button
                          key={island.island_id}
                          onClick={() => targetLessonId && navigate(`/lesson-modules/${targetLessonId}`)}
                          disabled={!targetLessonId}
                          className="w-full text-left group disabled:cursor-default"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-9 h-9 rounded-lg flex items-center justify-center text-base flex-shrink-0"
                              style={{ background: island.leafColor + '60' }}
                            >
                              {island.icon || '🌱'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-semibold text-gray-800 truncate group-hover:text-purple-700">
                                  {island.name || `Ilha ${idx + 1}`}
                                </span>
                                <span
                                  className="text-xs font-bold ml-2 flex-shrink-0"
                                  style={{ color: island.potColor }}
                                >
                                  {island.progress}%
                                </span>
                              </div>
                              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-1.5 rounded-full transition-all duration-500"
                                  style={{ width: `${island.progress}%`, background: island.potColor }}
                                />
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProgressDashboard;
